import { Layout } from '@/components/layout/Layout';
import { motion } from 'framer-motion';
import { Truck, Info, Zap, Globe } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';

export default function Shipping() {
  return (
    <Layout>
      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex items-center gap-4 mb-8">
            <div className="p-3 rounded-2xl bg-primary/10">
              <Truck className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-slate-900 dark:text-white">Shipping Policy</h1>
          </div>

          <Alert className="mb-8 bg-blue-500/5 border-blue-500/20 text-blue-600 dark:text-blue-400">
            <Info className="h-4 w-4" />
            <AlertDescription className="font-medium">
              NexQR provides digital software services. No physical products are shipped.
            </AlertDescription>
          </Alert>

          <div className="prose dark:prose-invert max-w-none space-y-8">
            <section className="surface-elevated p-8 rounded-3xl space-y-4 shadow-sm border border-border/50">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Zap className="h-6 w-6 text-yellow-500" />
                Digital Delivery
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                All services provided by NexQR are digital in nature. Upon successful payment for a subscription or feature, access is granted <strong>immediately</strong> to your account.
              </p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Subscription activations are instantaneous.</li>
                <li>QR code generation and dynamic redirect features are enabled as soon as the transaction is confirmed by our payment gateways (Stripe/Razorpay).</li>
                <li>A digital invoice and confirmation email will be sent to your registered email address within minutes of purchase.</li>
              </ul>
            </section>

            <section className="p-8 space-y-4">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Globe className="h-6 w-6 text-primary" />
                Global Availability
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                As a cloud-based SaaS platform, NexQR is available globally. There are no shipping charges, customs duties, or physical delivery constraints. 
              </p>
              <p className="text-muted-foreground leading-relaxed">
                If you experience any delay in accessing your purchased features after a successful payment, please contact our support team at <strong>support@nexqr.online</strong> with your transaction ID.
              </p>
            </section>

            <section className="p-8 bg-muted/30 rounded-3xl space-y-4">
              <h2 className="text-xl font-bold">Important Notes:</h2>
              <ul className="list-none space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  <span>NexQR does not sell physical QR code stickers or hardware.</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  <span>Users are responsible for printing their own generated QR codes.</span>
                </li>
                <li className="flex items-start gap-2">
                  <div className="h-1.5 w-1.5 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                  <span>Ensure your email address is correct during checkout to receive your access credentials.</span>
                </li>
              </ul>
            </section>
          </div>
        </motion.div>
      </div>
    </Layout>
  );
}
